import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, Camera, AlertCircle, RotateCw } from "lucide-react";

/**
 * QrScanner — full-screen camera modal that scans the client's QR code.
 *
 * Works in mobile Safari and Chrome over HTTPS without any app install. Uses
 * the html5-qrcode library which wraps getUserMedia + decoding.
 *
 * Props:
 *   open      — bool; when false, camera is fully stopped
 *   onClose   — () => void
 *   onResult  — (clientId: string) => void  — called once with a parsed id
 *
 * QR payload formats accepted (any of):
 *   "angiolini:c:<24-hex>"      — current canonical format from BonusCard
 *   "angiolini-vip-<24-hex>"    — legacy
 *   "<24-hex>"                  — raw ObjectId, for manual entry / paste
 */
const ELEMENT_ID = "angiolini-qr-scanner";

function extractClientId(text) {
  if (!text) return null;
  const trimmed = String(text).trim();
  // Match a 24-character hex ObjectId at the end of the string
  const m = trimmed.match(/([a-fA-F0-9]{24})$/);
  return m ? m[1] : null;
}

export default function QrScanner({ open, onClose, onResult }) {
  const scannerRef = useRef(null);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [manualValue, setManualValue] = useState("");

  // Start camera on open, stop on close
  useEffect(() => {
    if (!open || manualMode) return undefined;
    let cancelled = false;

    const start = async () => {
      setError(""); setStarting(true);
      try {
        const html5 = new Html5Qrcode(ELEMENT_ID, { verbose: false });
        scannerRef.current = html5;

        const handleSuccess = (decoded) => {
          const clientId = extractClientId(decoded);
          if (clientId) {
            try {
              const p = html5.stop();
              if (p && typeof p.catch === "function") p.catch(() => {});
            } catch { /* already stopped */ }
            onResult(clientId);
          }
        };

        await html5.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 260 }, aspectRatio: 1.0 },
          handleSuccess,
          () => { /* swallow per-frame failures */ },
        );

        if (cancelled) {
          await html5.stop().catch(() => {});
        }
      } catch (e) {
        if (e?.name === "NotAllowedError" || e?.message?.includes("Permission")) {
          setError("Доступ к камере запрещён. Разрешите камеру в настройках браузера.");
        } else if (e?.name === "NotFoundError") {
          setError("Камера не найдена на этом устройстве.");
        } else if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
          setError("Камера работает только по HTTPS. Откройте сайт через https://");
        } else {
          setError("Не удалось запустить камеру: " + (e?.message || e));
        }
      } finally {
        setStarting(false);
      }
    };

    start();
    return () => {
      cancelled = true;
      if (scannerRef.current) {
        // html5-qrcode throws synchronously if stop() is called when not running.
        // Suppress both sync and async errors — the camera is meant to be cleaned up best-effort.
        try {
          const stopP = scannerRef.current.stop();
          if (stopP && typeof stopP.catch === "function") stopP.catch(() => {});
        } catch { /* not running */ }
        try {
          const clearP = scannerRef.current.clear();
          if (clearP && typeof clearP.catch === "function") clearP.catch(() => {});
        } catch { /* nothing to clear */ }
        scannerRef.current = null;
      }
    };
  }, [open, manualMode, onResult]);

  if (!open) return null;

  const submitManual = () => {
    const id = extractClientId(manualValue);
    if (!id) {
      setError("Неверный формат ID. Введите 24-символьный код (или полную строку QR).");
      return;
    }
    onResult(id);
  };

  return (
    <div data-testid="qr-scanner-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.94)", zIndex: 1000, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", color: "white", fontFamily: "Manrope", fontSize: "14px", fontWeight: 600 }}>
          <Camera size={18} style={{ color: "#C5A059" }} /> {manualMode ? "Ввод ID вручную" : "Сканирование QR-карты"}
        </div>
        <button data-testid="scanner-close-btn" onClick={onClose} aria-label="Закрыть"
          style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "50%", width: "36px", height: "36px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "white" }}>
          <X size={18} />
        </button>
      </div>

      {/* Camera frame OR manual input */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px" }}>
        {!manualMode && (
          <div style={{ width: "100%", maxWidth: "420px" }}>
            <div id={ELEMENT_ID} data-testid="qr-camera"
              style={{ width: "100%", aspectRatio: "1/1", background: "#0F0F0F", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(197,160,89,0.25)", position: "relative" }} />
            <div style={{ marginTop: "16px", textAlign: "center", color: "rgba(255,255,255,0.65)", fontFamily: "Manrope", fontSize: "13px", lineHeight: 1.5 }}>
              {starting && "Запуск камеры…"}
              {!starting && !error && "Наведите рамку на QR-код клиента"}
            </div>
            {error && (
              <div style={{ marginTop: "12px", background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)", borderRadius: "8px", padding: "12px 14px", color: "#FCA5A5", fontFamily: "Manrope", fontSize: "13px", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                <AlertCircle size={16} style={{ flexShrink: 0, marginTop: "1px" }} />
                <div>{error}</div>
              </div>
            )}
          </div>
        )}

        {manualMode && (
          <div style={{ width: "100%", maxWidth: "420px" }}>
            <div style={{ color: "rgba(255,255,255,0.7)", fontFamily: "Manrope", fontSize: "13px", marginBottom: "10px", lineHeight: 1.5 }}>
              Если камера недоступна — попросите клиента продиктовать ID из приложения или открыть QR-карту и переписать код.
            </div>
            <input data-testid="scanner-manual-input" autoFocus value={manualValue} onChange={(e) => setManualValue(e.target.value)}
              placeholder="angiolini:c:507f1f77bcf86cd799439011  или 24-значный ID"
              onKeyDown={(e) => { if (e.key === "Enter") submitManual(); }}
              style={{ width: "100%", background: "#141414", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "6px", padding: "14px", color: "white", fontFamily: "monospace", fontSize: "13px", outline: "none", marginBottom: "10px" }} />
            {error && <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#FCA5A5", marginBottom: "10px" }}>{error}</div>}
            <button data-testid="scanner-manual-submit" onClick={submitManual}
              style={{ width: "100%", background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "6px", padding: "12px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Открыть карточку клиента
            </button>
          </div>
        )}
      </div>

      {/* Footer toggle */}
      <div style={{ padding: "16px 20px", borderTop: "1px solid rgba(255,255,255,0.06)", textAlign: "center" }}>
        <button data-testid="scanner-toggle-mode" onClick={() => { setManualMode(!manualMode); setError(""); }}
          style={{ background: "transparent", border: "none", color: "#C5A059", fontFamily: "Manrope", fontSize: "13px", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <RotateCw size={13} />
          {manualMode ? "← Назад к сканированию" : "Ввести ID вручную →"}
        </button>
      </div>
    </div>
  );
}
